import { useMemo } from 'react'

import { Dance, DanceWithEvents } from '@/types'

import { backendQueryHook, entityCreateHook, entityDeleteHook, entityListQueryHook, entityUpdateHook, graphql, setupServiceUpdateFragment, useServiceEvents } from '@/backend'

import { searchList } from '@/libraries/common/listSearch'
import { compareBy, sorted } from '@/utils/sorted'

setupServiceUpdateFragment(
  'dances',
  `fragment DanceFragment on Dance {
    _id, name, description, remarks, duration, prelude, formation, category, instructions, slideStyleId
    wikipageName
  }`,
)

export type WritableDanceProperty = Exclude<keyof Dance, '_id' | '_versionId' | '_versionNumber' | '__typename' | 'teachedIn'>

export const useDanceCategories = entityListQueryHook('dances', graphql(`
query getDanceCategories {
  danceCategories
}`))

export const useDances = entityListQueryHook('dances', graphql(`
query getDances {
  dances {
    _id, _versionId, name, category,
    wikipageName
    wikipage {
      categories
    }
    events {
      _id
      _versionId
      name
    }
  }
}`))

export const useDanceVersions = backendQueryHook(graphql(`
query getDanceVersions($id: ID!) {
  dance(id: $id) {
    _id, _versionId, _versionNumber,
    name
    versionHistory {
      calendar {
        date
        versions {
          _versionId
          _versionNumber
          _updatedAt
        }
      }
    }
  }
}`))

export const useDance = backendQueryHook(graphql(`
query getDance($id: ID!, $versionId: ID) {
  dance(id: $id, versionId: $versionId) {
    _id, _versionId, _versionNumber, name, description, remarks, duration, prelude, formation, source, category, instructions, slideStyleId, tags
    wikipageName
    wikipage {
      _id
      _fetchedAt
      status
      categories
      formations
      content
    }
    events {
      _id
      _versionId
      name
    }
    formationDiagrams {
      _id
      ballroom {
        _id
        venueName
        roomName
        map
      }
      description
      diagram
    }
  }
}`), ({ refetch, variables }) => {
  if (variables === undefined) throw new Error('Unknown dance id')
  useCallbackOnDanceChanges(variables.id, refetch)
})

function useCallbackOnDanceChanges(danceId, callback) {
  const callbacks = useMemo(() => {
    const updateFn = () => {
      // console.log('Dance has changed, running callback')
      callback()
    }
    return {
      created: updateFn,
      updated: updateFn,
      removed: updateFn,
    }
  }, [callback])
  useServiceEvents('dances', `dances/${danceId}`, callbacks)
}

export const useCreateDance = entityCreateHook('dances', graphql(`
mutation createDance($dance: DanceInput!) {
  createDance(dance: $dance) {
    _id, _versionId, _versionNumber, name, description, remarks, duration, prelude, formation, source, category, instructions, slideStyleId
    wikipageName
    wikipage {
      _id
      _fetchedAt
      status
      categories
      formations
      content
    }
    events {
      _id
      _versionId
      name
    }
    formationDiagrams {
      _id
      ballroom {
        _id
        venueName
        roomName
        map
      }
      description
      diagram
    }
  }
}`))

export const usePatchDance = entityUpdateHook('dances', graphql(`
mutation patchDance($id: ID!, $dance: JSONPatch!) {
  patchDance(id: $id, dance: $dance) {
    _id, _versionId, _versionNumber, name, description, remarks, duration, prelude, formation, source, category, instructions, slideStyleId, tags
    wikipageName
    wikipage {
      _id
      _fetchedAt
      status
      categories
      formations
      content
    }
    events {
      _id
      _versionId
      name
    }
    formationDiagrams {
      _id
      ballroom {
        _id
        venueName
        roomName
        map
      }
      description
      diagram
    }
  }
}`))

export const useDeleteDance = entityDeleteHook('dances', graphql(`
mutation deleteDance($id: ID!) {
  deleteDance(id: $id) {
    _id, _versionId, _versionNumber, name, description, remarks, duration, prelude, formation, source, category, instructions, slideStyleId, tags
    wikipageName
    wikipage {
      _id
      _fetchedAt
      status
      categories
      formations
      content
    }
    events {
      _id
      _versionId
      name
    }
    formationDiagrams {
      _id
      ballroom {
        _id
        venueName
        roomName
        map
      }
      description
      diagram
    }
  }
}`))

export function filterDances<T extends Pick<DanceWithEvents, 'name' | 'category' | 'events'>>(dances: T[], searchString: string, categoryFilter?: string): T[] {
  const filtered = searchList(
    sortDances(dances),
    searchString,
    dance => dance.name,
    dance => dance.category ?? '',
    dance => dance.events.map(e => e.name).join(' '),
  )
  if (categoryFilter !== undefined) {
    return filtered.filter(dance => dance.category === categoryFilter)
  }
  return filtered
}

export function sortDances<T extends Pick<Dance, 'name'>>(dances: T[]): T[] {
  return sorted(dances, compareDances)
}

const compareDances = compareBy(danceSortKey)

function danceSortKey(dance: Pick<Dance, 'name'>): string {
  return dance.name.replace(/^(an?|the) +/i, '')
}
